// Constants
import { NAV_PATHS } from '@/utils/constants';

// Types
import Nullable from '@/types/nullable';
import ThemeMode, { ThemeVersionInfo } from '@/types/theme-mode';
import User from '@/types/user';
import UserPreferences from '@/types/user-preferences';

// These are the allowed paths for the light theme. Update this list with the newly implemented paths.
export const LIGHT_MODE_NAV_PATHS = [
  NAV_PATHS.COMPARISONS,
  NAV_PATHS.COMPARISON,
  NAV_PATHS.CONTACTS,
  NAV_PATHS.SEARCH,
  NAV_PATHS.MODELS,
  NAV_PATHS.SETTINGS,
  NAV_PATHS.ADMIN,
  NAV_PATHS.HOUSEHOLDS,
  NAV_PATHS.ANNOUNCEMENTS,
  NAV_PATHS.PLANNING,
];

export const getTheme = (pageInfo: { pathname: string; preferences?: UserPreferences }): ThemeMode => {
  if (!pageInfo || !pageInfo.pathname || !pageInfo.preferences) {
    return ThemeMode.DEFAULT;
  }

  const {
    pathname,
    preferences: { theme: userTheme },
  } = pageInfo;

  const userPrefersLightTheme = userTheme === ThemeMode.LIGHT;
  if (!userPrefersLightTheme) {
    return ThemeMode.DEFAULT;
  }

  const pathAllowsLightMode = LIGHT_MODE_NAV_PATHS.some(path => pathname.includes(path));
  if (pathAllowsLightMode) {
    return ThemeMode.LIGHT;
  }

  return ThemeMode.DEFAULT;
};

/**
 * Retrieves the theme version and whether it is a light version based on the user's preferences.
 *
 * Used by the wrapper component to determine the theme version to render.
 *
 * @param user - The user object containing preferences.
 * @returns An object containing the theme version and a boolean indicating if it is a light version.
 * @returns.userTheme - The current theme version of the user.
 * @returns.isLightVersion - A boolean indicating if the theme version is light.
 */
export const getThemeVersion = (user?: Nullable<User>): ThemeVersionInfo => {
  const userTheme = user?.preferences?.theme || ThemeMode.DEFAULT;

  return {
    userTheme,
    isLightVersion: userTheme === ThemeMode.LIGHT,
  };
};
